import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "./dialog";
import { Button } from "./button";

type ButtonColor = "red" | "blue" | "amber" | "emerald" | "sky" | "indigo" | "zinc";

export type ConfirmDialogProps = {
    /** Whether the dialog is open. */
    open: boolean;
    /** Called when the dialog is dismissed (cancel button or backdrop click). */
    onClose: () => void;
    /**
     * Called when the confirm button is pressed. May return a promise — the
     * confirm button shows a loading state while it resolves.
     */
    onConfirm: () => void | Promise<unknown>;
    /** Headline of the dialog. */
    title: string;
    /** Optional supportive text under the title. */
    description?: React.ReactNode;
    /** Label for the destructive/confirming action. Defaults to translation key `button.confirm`. */
    confirmLabel?: string;
    /** Label for the cancel button. Defaults to translation key `button.cancel`. */
    cancelLabel?: string;
    /** Color of the confirm button. Default `red` for destructive actions. */
    confirmColor?: ButtonColor;
    /** Pass-through size for the underlying `<Dialog>`. */
    size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
    /** Optional extra body content rendered between description and actions. */
    children?: React.ReactNode;
};

/**
 * A generic confirmation dialog. Designed to replace the dozen near-identical
 * `delete-*.tsx`, `expire-*.tsx`, `disable-/enable-subnet.tsx` dialogs:
 * supply title + description + onConfirm and the loading state, error
 * handling, and visual style come for free.
 */
export function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmLabel,
    cancelLabel,
    confirmColor = "red",
    size = "lg",
    children,
}: ConfirmDialogProps) {
    const [tg] = useTranslation();
    const [loading, setLoading] = React.useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={loading ? () => undefined : onClose} size={size}>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
            {children}
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {cancelLabel ?? tg("button.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button color={confirmColor} loading={loading} onClick={handleConfirm}>
                    {confirmLabel ?? tg("button.confirm", { defaultValue: "Confirm" })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
